<template>
  <v-row class="align-center justify-center" v-if="!hasSingleContext">
    <v-col
      cols="12"
      class="d-flex flex-column justify-center align-center ga-2 mb-6"
    >
      <h2 v-if="$userProfile" class="text-h3" data-cc="landing-greeting">
        {{ $t('generic.greeting') }}, {{ $userProfile.firstname }}
      </h2>

      <p class="text-h6 text-medium-emphasis">
        {{ $t('mainNavigation.landingPage.chooseRole') }}
      </p>
    </v-col>

    <v-col cols="12" class="d-flex align-center justify-center flex-wrap ga-6">
      <v-card
        v-for="context in filteredContext"
        :key="context.name"
        :to="{ name: context.path }"
        height="300"
        width="300"
        class="context-card d-flex align-center justify-center text-center border"
        flat
        data-cc="context-selector"
        :data-item="context.code"
      >
        <v-card-text class="position-relative z">
          <v-avatar size="120">
            <v-icon
              data-cc="context-card-icon"
              class="context-card__icon ic-48"
              >{{ context.svg }}</v-icon
            >
          </v-avatar>

          <div
            data-cc="context-card-title"
            class="context-card__title text-h4 mt-6"
          >
            {{ context.text || $t(`mainNavigation.contexts.${context.name}`) }}
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script setup>
import { computed, onMounted } from 'vue';

import useScopesStore from '@/stores/scopes';
import { usePermissions } from '@/composables/usePermissions';
import { isProduction } from '@/utils/environment';

const { isSuperAdmin, contexts, hasSingleContext } = usePermissions();

const { updateSelectedScope } = useScopesStore();

// Hooks
onMounted(() => {
  updateSelectedScope({ type: null, data: null });
});

// Design Guide Context injection
const designContext = [
  {
    index: 3,
    code: 'DESIGN',
    icon: 'huge:color',
    svg: '$marker',
    text: 'Design Guide',
    path: 'design'
  }
];

const shouldShowDesignContext = computed(() => {
  return isSuperAdmin.value && !isProduction;
});

const filteredContext = computed(() => {
  if (shouldShowDesignContext.value) {
    return [...contexts.value, ...designContext];
  }

  return contexts.value;
});
</script>

<style lang="scss" scoped>
.context-card {
  // We will use the ripple effect to create the overlay effect
  // As the ripple effect uses the activated-opacity variable,
  // we need to tone it down to reach the desire effect of opacity of 0.16
  // calc(0.25 * var(--v-theme-overlay-multiplier))
  --v-theme-overlay-multiplier: 0.65;

  &__icon {
    --icon-shadow-color: var(--v-theme-primary-100);
    color: rgb(var(--v-theme-primary-300));
    transition: color 0.3s ease-in-out;

    &.v-theme--dark {
      --icon-shadow-color: var(--v-theme-primary-300);
      color: rgb(var(--v-theme-primary));
    }
  }

  :deep() {
    .v-card-text {
      z-index: 1;
    }

    svg * {
      transition:
        fill 0.3s ease-in-out,
        opacity 0.3s ease-in-out;
    }

    .v-ripple__container,
    .v-card__overlay {
      color: rgb(var(--v-theme-primary));
    }

    .v-avatar {
      --v-theme-avatar: var(--v-theme-primary-50);
      transition: background-color 0.5s ease-in-out;

      &.v-theme--dark {
        --v-theme-avatar: var(--v-theme-primary-75);
      }
    }
  }

  &:focus-visible,
  &:hover,
  &:active {
    --icon-shadow-opacity: 0.6;

    .context-card__title {
      color: rgb(var(--v-theme-primary), var(--v-high-emphasis-opacity));
    }
  }

  &:focus-visible :deep(),
  &:hover :deep() {
    .v-avatar {
      --v-theme-avatar: var(--v-theme-white);

      &.v-theme--dark {
        --v-theme-avatar: var(--v-theme-primary-50);
      }
    }

    .v-card__overlay {
      --v-hover-opacity: calc(var(--v-activated-opacity) * 1.5);
      --v-focus-opacity: calc(var(--v-activated-opacity) * 1.5);
    }
  }

  &:active :deep(.v-card__overlay) {
    --v-hover-opacity: 0;
  }

  &:active .context-card__icon {
    color: rgb(var(--v-theme-gray-800));
  }
}
</style>
